import React from 'react'
import { Modal, Input, InputNumber, Select, Switch, Tabs, Button, Spin, message } from 'antd'
import imgRobot from '@img/robot.png'
import flyersService from '../../services/flyers'

// templates are saved with English names (the file names), the admin sees them in Hebrew
export const hebrewNames: { [name: string]: string } = {
    'best-for-families': 'הכי טוב למשפחה',
    'child-development': 'התפתחות הילד',
    'complementary-medicine': 'רפואה משלימה',
    'mediwho-all-inclusive': 'מדיהו הכל כלול',
    'mediwho-assistant': 'מדיהו אססיסטנט',
    'mediwho-gold-plus': 'מדיהו גולד פלוס',
    'mediwho-gold': 'מדיהו גולד',
    'mediwho-hospital-plus': 'מדיהו אשפוז פלוס',
    'mediwho-simple-smile': 'מדיהו סימפל סמייל',
    'why-mediwho': 'למה מדיהו'
}

// same numbers as api/app/Constants/Flyers.js
const DEFAULT_TEXT_BOX = { x: 2.74, y: 92.88, w: 45, h: 4.9 }
// square in real pixels (A4 template): w % of the width = h % of the height, so w = h * 1.414
const DEFAULT_LOGO_BOX = { x: 88.71, y: 91.7, w: 9.63, h: 7.43 }
// the right edge of the name is at x + w, its top at y (h is not used, the box is as big as the name). Same as api/app/Constants/Flyers.js
const DEFAULT_NAME_BOX = { x: 67.5, y: 94, w: 20, h: 3 }

const MIN_SIZE = 2
// same as api/app/Constants/Flyers.js
const FONTS = ['Rubik', 'Heebo']
// fallback width for old flyers without a saved width. width in px of the real template images, the preview shows the small thumb so its own width can't be used
const TEMPLATE_WIDTH = 2481
const MIN_FONT_SIZE = 10
const MAX_FONT_SIZE = 300
// same icons, size and gap as api/app/Modules/FlyerImage.js (em of the font size)
const ICON_PATHS = {
    phone: (
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    ),
    mail: (
        <>
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
        </>
    )
}
// icon size and gap in em of the font size. If you change them here, change them in the api too: ICON_SIZE and ICON_GAP in api/app/Modules/FlyerImage.js
const ICON_SIZE = 1.1
const ICON_GAP = 0.3
// space between two lines, in em of the font size. Same as LINE_GAP in api/app/Modules/FlyerImage.js
const LINE_GAP = 0.4
const IconLine = ({ icon, text }: { icon: 'phone' | 'mail'; text: string }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <svg
            // cropped to the drawn lines (2px stroke), so the icon fills its box
            viewBox="1 1 22 22"
            style={{ width: ICON_SIZE + 'em', height: ICON_SIZE + 'em', marginRight: ICON_GAP + 'em', flexShrink: 0 }}
            fill="none"
            stroke="#000"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            {ICON_PATHS[icon]}
        </svg>
        <span>{text}</span>
    </div>
)
const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)
const round = (v: number) => Math.round(v * 100) / 100

type Box = { x: number; y: number; w: number; h: number }
// frame color and light fill of each box
const BOX_COLORS = {
    name_box: { frame: '#1890ff', fill: 'rgba(24, 144, 255, 0.15)' },
    text_box: { frame: '#52c41a', fill: 'rgba(82, 196, 26, 0.15)' },
    logo_box: { frame: '#fa541c', fill: 'rgba(250, 84, 28, 0.15)' }
}
type DragType = 'move' | 'resize'

class FlyerModal extends React.Component<any, any> {
    // the api fills in the defaults, so the flyer always has every box and font
    state = {
        name: (hebrewNames[this.props.flyer.name] || this.props.flyer.name) as string,
        is_active: !!this.props.flyer.is_active,
        text_box: this.props.flyer.text_box as Box,
        logo_box: this.props.flyer.logo_box as Box,
        name_box: this.props.flyer.name_box as Box,
        font: this.props.flyer.font as string,
        font_size: this.props.flyer.font_size as number,
        text_font: this.props.flyer.text_font as string,
        text_font_size: this.props.flyer.text_font_size as number,
        tab: 'logo_box' as 'name_box' | 'logo_box' | 'text_box',
        saving: false,
        imgState: 'loading' as 'loading' | 'loaded' | 'failed',
        // preview: hide the box frame, show only the image with the example
        preview: false,
        downloading: false
    }

    previewRef = React.createRef<HTMLDivElement>()
    drag: { type: DragType; startX: number; startY: number; box: Box } | null = null

    setBox = (box: Box) => this.setState({ [this.state.tab]: box } as any)

    startDrag = (type: DragType) => (e: React.PointerEvent<HTMLDivElement>) => {
        e.stopPropagation()
        // capture keeps the move events coming even when the pointer leaves the box
        e.currentTarget.setPointerCapture(e.pointerId)
        this.drag = { type, startX: e.clientX, startY: e.clientY, box: this.state[this.state.tab] }
    }

    onDrag = (e: React.PointerEvent<HTMLDivElement>) => {
        const drag = this.drag
        const preview = this.previewRef.current
        if (!drag || !preview) return
        const rect = preview.getBoundingClientRect()
        // pointer move in % of the preview size
        const dx = ((e.clientX - drag.startX) / rect.width) * 100
        const dy = ((e.clientY - drag.startY) / rect.height) * 100
        const { x, y, w, h } = drag.box
        if (drag.type === 'move') {
            this.setBox({ x: round(clamp(x + dx, 0, 100 - w)), y: round(clamp(y + dy, 0, 100 - h)), w, h })
            return
        }
        if (this.state.tab === 'logo_box') {
            // the logo box stays square in real pixels: h % = w % * (image width / image height)
            const k = rect.width / rect.height
            const newW = clamp(w - dx, MIN_SIZE, Math.min(x + w, (100 - y) / k))
            this.setBox({ x: round(x + w - newW), y, w: round(newW), h: round(newW * k) })
            return
        }
    }

    endDrag = () => {
        this.drag = null
    }

    reset = () => {
        const { tab } = this.state
        this.setBox(tab === 'name_box' ? DEFAULT_NAME_BOX : tab === 'text_box' ? DEFAULT_TEXT_BOX : DEFAULT_LOGO_BOX)
    }

    // the api draws the real flyer (current unsaved values, example logo or name, and text) and the browser downloads it
    download = async () => {
        const { tab } = this.state
        const body = this.buildBody()
        this.setState({ downloading: true })
        try {
            // the api prints the name only when there is no logo: name tab = name without logo, other tabs = logo without name
            if (tab !== 'name_box') body.append('logo', await (await fetch(imgRobot)).blob(), 'logo.png')
            const res = await flyersService.preview(this.props.flyer.id, body)
            const link = document.createElement('a')
            link.href = URL.createObjectURL(res.data)
            link.download = 'flyer.jpg'
            link.click()
            URL.revokeObjectURL(link.href)
        } catch (e) {
            message.error('הורדת התצוגה נכשלה')
        }
        this.setState({ downloading: false })
    }

    // boxes and fonts, the same fields for the preview download and for save
    buildBody = () => {
        const body = new FormData()
        const boxes = ['text_box', 'logo_box', 'name_box']
        const fonts = ['font', 'font_size', 'text_font', 'text_font_size']
        boxes.forEach(k => body.append(k, JSON.stringify(this.state[k])))
        fonts.forEach(k => body.append(k, String(this.state[k])))
        return body
    }

    save = async () => {
        const { name, is_active } = this.state
        const body = this.buildBody()
        // a Hebrew name from the map goes to the api as its English name, a new typed name is saved as typed
        const englishName = Object.keys(hebrewNames).find(key => hebrewNames[key] === name.trim())
        body.append('name', englishName || name.trim())
        body.append('is_active', is_active ? '1' : '0')
        this.setState({ saving: true })
        const ok = await this.props.onSave(this.props.flyer.id, body)
        // on success the parent closes the modal
        if (!ok) this.setState({ saving: false })
    }

    render() {
        const { flyer, onClose } = this.props
        const {
            name,
            is_active,
            tab,
            saving,
            font,
            font_size,
            text_font,
            text_font_size,
            preview: isPreview,
            downloading,
            imgState
        } = this.state
        // the box and the example text need the image size, so wait for it
        const showBox = imgState !== 'loading'
        const isContact = tab === 'text_box'
        // phone + mail are always shown. The logo and the name show only in their own tab, also in preview (preview only removes the frame)
        const others = (['text_box', 'logo_box', 'name_box'] as Array<'text_box' | 'logo_box' | 'name_box'>).filter(
            k => k !== tab && k === 'text_box'
        )
        // fixed font size in px of the real image, scaled down to the preview
        // 12px fallback until the image is loaded (or when it never loads), so the example text is never empty
        const previewEl = this.previewRef.current
        const scaled = (size: number) =>
            previewEl && imgState === 'loaded' ? (size * previewEl.offsetWidth) / (flyer.width || TEMPLATE_WIDTH) : 12
        // phone + mail have their own font and size (contact tab). The font and size of the name are in the name tab
        const exampleFontSize = scaled(text_font_size)
        // draws one box with its example. Only the selected box (tab) has a frame and can be dragged
        const renderBox = (which: 'text_box' | 'logo_box' | 'name_box') => {
            const active = which === tab
            const isTextBox = which === 'text_box'
            const isNameBox = which === 'name_box'
            // the text and name boxes are as big as their text
            const fit = isTextBox || isNameBox
            const boxes: Box = this.state[which]
            const frameOff = isPreview || !active
            return (
                <div
                    onPointerDown={active ? this.startDrag('move') : undefined}
                    onPointerMove={this.onDrag}
                    onPointerUp={this.endDrag}
                    style={{
                        position: 'absolute',
                        // the name box is fixed by its right edge (x + w) and grows to the left, like in the api render
                        left: isNameBox ? undefined : boxes.x + '%',
                        right: isNameBox ? 100 - boxes.x - boxes.w + '%' : undefined,
                        top: boxes.y + '%',
                        // the text and name boxes wrap their example (3px padding), the logo box keeps its saved size
                        width: fit ? undefined : boxes.w + '%',
                        height: fit ? undefined : boxes.h + '%',
                        padding: fit ? 3 : 0,
                        // border + padding (5px) are pulled outside, so the text starts exactly at x/y like in the api render
                        margin: fit ? -5 : -2,
                        // content-box: the 2px border sits outside the box, so the content gets the exact box size like in the api render
                        boxSizing: 'content-box',
                        // the logo sticks to the right edge of its box, like in the api render
                        display: fit ? undefined : 'flex',
                        justifyContent: 'flex-end',
                        // in preview the frame is transparent (same size, so nothing jumps) and can't be dragged
                        border: '2px solid ' + (frameOff ? 'transparent' : BOX_COLORS[which].frame),
                        // light background in the color of the frame
                        background: frameOff ? 'transparent' : BOX_COLORS[which].fill,
                        cursor: frameOff ? 'default' : 'move',
                        pointerEvents: frameOff ? 'none' : undefined,
                        touchAction: 'none'
                    }}
                >
                    {isTextBox && (
                        // example agent phone + mail
                        <div
                            style={{
                                fontFamily: text_font + ', sans-serif',
                                fontSize: exampleFontSize,
                                lineHeight: 1.1,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                color: '#000',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                gap: LINE_GAP + 'em'
                            }}
                        >
                            <IconLine icon="phone" text="050-1234567" />
                            <IconLine icon="mail" text="name@example.com" />
                        </div>
                    )}
                    {/* example agent name, flyer font and size like nameLayer in api/app/Modules/FlyerImage.js (no shrink here) */}
                    {isNameBox && (
                        <span
                            style={{
                                fontFamily: font + ', sans-serif',
                                fontSize: scaled(font_size),
                                lineHeight: 1.1,
                                display: 'block',
                                whiteSpace: 'nowrap',
                                color: '#000'
                            }}
                        >
                            ישראל ישראלי
                        </span>
                    )}
                    {/* example logo, full box height, stuck to the right */}
                    {which === 'logo_box' && <img src={imgRobot} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'right' }} draggable={false} />}
                    {which === 'logo_box' && active && !isPreview && (
                        <div
                            onPointerDown={this.startDrag('resize')}
                            onPointerMove={this.onDrag}
                            onPointerUp={this.endDrag}
                            style={{
                                position: 'absolute',
                                left: -6,
                                bottom: -6,
                                width: 12,
                                height: 12,
                                background: '#fff',
                                border: '2px solid #fa541c',
                                cursor: 'nesw-resize',
                                touchAction: 'none'
                            }}
                        />
                    )}
                </div>
            )
        }
        return (
            <Modal
                visible
                title="עריכת פלייר"
                width={720}
                onCancel={onClose}
                okText="שמירה"
                cancelText="ביטול"
                onOk={this.save}
                okButtonProps={{ disabled: !name.trim(), loading: saving }}
            >
                <div style={{ direction: 'rtl' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16 }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>שם הפלייר</div>
                            <Input
                                placeholder="שם הפלייר"
                                value={name}
                                onChange={e => this.setState({ name: e.target.value })}
                            />
                        </div>
                        <span>
                            פעיל <Switch checked={is_active} onChange={v => this.setState({ is_active: v })} />
                        </span>
                    </div>
                    {/* antd 3 positions the ink bar from the left, so the tabs are ltr with the order flipped: the first tab (logo) ends up on the right */}
                    <div style={{ direction: 'ltr' }}>
                        <Tabs activeKey={tab} onChange={k => this.setState({ tab: k as any })}>
                            <Tabs.TabPane tab="מיקום פרטי קשר" key="text_box" />
                            <Tabs.TabPane tab="מיקום שם" key="name_box" />
                            <Tabs.TabPane tab="מיקום לוגו" key="logo_box" />
                        </Tabs>
                    </div>
                    {tab !== 'logo_box' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                            <span>
                                {isContact ? 'גופן פרטי קשר' : 'גופן השם'}{' '}
                                <Select
                                    value={isContact ? text_font : font}
                                    style={{ width: 120 }}
                                    onChange={(v: string) => this.setState({ [isContact ? 'text_font' : 'font']: v } as any)}
                                >
                                    {FONTS.map(f => (
                                        <Select.Option key={f} value={f}>
                                            {f}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </span>
                            <span>
                                {isContact ? 'גודל גופן פרטי קשר (px)' : 'גודל גופן השם (px)'}{' '}
                                <InputNumber
                                    value={isContact ? text_font_size : font_size}
                                    min={MIN_FONT_SIZE}
                                    max={MAX_FONT_SIZE}
                                    precision={0}
                                    onChange={(v: any) =>
                                        typeof v === 'number' &&
                                        this.setState({ [isContact ? 'text_font_size' : 'font_size']: v } as any)
                                    }
                                />
                            </span>
                        </div>
                    )}
                    {/* ltr, because the box x is measured from the left edge of the image */}
                    <div
                        ref={this.previewRef}
                        style={{
                            position: 'relative',
                            direction: 'ltr',
                            width: '70%',
                            margin: '0 auto',
                            userSelect: 'none'
                        }}
                    >
                        {/* onLoad re-renders, so the example text can be scaled to the preview */}
                        {imgState !== 'loaded' && (
                            // gray placeholder with a loader until the image is loaded
                            <div style={{ paddingTop: '140%', background: '#f0f0f0', position: 'relative' }}>
                                {imgState === 'loading' && (
                                    <Spin
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)'
                                        }}
                                    />
                                )}
                            </div>
                        )}
                        {imgState !== 'failed' && (
                            <img
                                src={flyer.thumbUrl}
                                alt={name}
                                style={{
                                    width: '100%',
                                    display: imgState === 'loaded' ? 'block' : 'none',
                                    border: '1px solid gray',
                                    boxShadow: '0px 3px 13px #808080'
                                }}
                                draggable={false}
                                onLoad={() => this.setState({ imgState: 'loaded' })}
                                onError={() => this.setState({ imgState: 'failed' })}
                            />
                        )}
                        {/* the other boxes first, without a frame, so the selected one stays on top */}
                        {showBox && others.map(k => <React.Fragment key={k}>{renderBox(k)}</React.Fragment>)}
                        {showBox && renderBox(tab)}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 16,
                            marginTop: 12
                        }}
                    >
                        <span>
                            תצוגה מקדימה <Switch checked={isPreview} onChange={v => this.setState({ preview: v })} />
                        </span>
                        <Button icon="download" loading={downloading} onClick={this.download}>
                            הורדה
                        </Button>
                        <Button icon="undo" onClick={this.reset}>
                            איפוס לברירת מחדל
                        </Button>
                    </div>
                </div>
            </Modal>
        )
    }
}

export default FlyerModal
